import React from 'react';
import PropTypes from 'prop-types';
import Pressable from "react-native-web/dist/exports/Pressable";
import View from "react-native-web/dist/exports/View";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import Platform from "react-native-web/dist/exports/Platform";
import { applyTextStyles, applyShadowToken, applyOuterBorder, useTheme } from '../ThemeProvider';
import buttonPropTypes from './propTypes';
import { a11yProps, clickProps, focusHandlerProps, getCursorStyle, linkProps, resolvePressableState, resolvePressableTokens, selectSystemProps, viewProps, wrapStringsInText, withLinkRouter, contentfulProps, createMediaQueryStyles, StyleSheet as StyleSheetUtils } from '../utils';
import { IconText } from '../Icon';
import { jsx as _jsx } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, focusHandlerProps, linkProps, viewProps, contentfulProps]);
const getOuterBorderOffset = _ref => {
  let {
    outerBorderGap = 0,
    outerBorderWidth = 0
  } = _ref;
  return outerBorderGap + outerBorderWidth;
};
const selectFlexAndWidthStyles = _ref2 => {
  let {
    width,
    flex
  } = _ref2;
  const styles = {};
  if (width !== undefined && width !== null) {
    styles.width = width;
  }
  if (width === '100%') {
    styles.flex = 1;
  } else if (flex !== undefined && flex !== null) {
    styles.flex = flex;
  }
  return styles;
};
const selectOuterContainerStyles = _ref3 => {
  let {
    opacity,
    outerBorderColor,
    outerBorderWidth,
    outerBorderGap,
    borderRadius,
    outerBackgroundColor
  } = _ref3;
  return {
    ...Platform.select({
      native: {
        alignSelf: 'flex-start'
      }
    }),
    backgroundColor: outerBackgroundColor,
    opacity,
    ...applyOuterBorder({
      outerBorderGap,
      outerBorderWidth,
      outerBorderColor,
      borderRadius
    })
  };
};
const selectOuterSizeStyles = _ref4 => {
  let {
    outerBorderGap,
    outerBorderWidth,
    width,
    height
  } = _ref4;
  // The inner container's bounding box is the bounding box of the button overall
  // so this many device pixels will sit outside of the overall bounding box
  const outerBorderOffset = getOuterBorderOffset({
    outerBorderGap,
    outerBorderWidth
  });
  const sizeStyles = {};
  // Apply width/height tokens: number === pixels, string === explicit units e.g. %
  if (typeof width === 'number' && width > 0 || typeof height === 'number' && height > 0) {
    sizeStyles.width = width ? width + outerBorderOffset * 2 : width;
    sizeStyles.height = height ? height + outerBorderOffset * 2 : height;
    return sizeStyles;
  }
  if (!width) {
    return {
      ...sizeStyles,
      // Wrap content, stopping a flex parent's default align-items: stretch stretching focus ring beyond content
      ...Platform.select({
        // width: fit-content isn't supported on Firefox; can't cascade props like CSS `width: fit-content; width: --moz-fit-content;`
        web: {
          display: 'inline-flex'
        }
      })
    };
  }

  // Ensure the negative margin doesn't make element off-centre
  if (Platform.OS === 'web') {
    sizeStyles.width = `calc(${width} + ${outerBorderOffset * 2}px)`;
    sizeStyles.height = `calc(${height} + ${outerBorderOffset * 2}px)`;
    return sizeStyles;
  }
  // Can't use calc() on native but (unlike on web) flexGrow fills the container width here
  if (width === '100%') {
    sizeStyles.flexGrow = 1;
    return sizeStyles;
  }

  // Slight offset not such a concern if % width < 100%, as the button isn't centered anyway.
  // If centering support is added (e.g. alignSelf: center), set marginHorizontal 0 when centered.
  sizeStyles.width = width;
  sizeStyles.height = height;
  return sizeStyles;
};
const selectInnerContainerStyles = _ref5 => {
  let {
    backgroundColor,
    paddingLeft,
    paddingRight,
    paddingTop,
    paddingBottom,
    shadow,
    borderWidth,
    borderLeftWidth,
    borderRightWidth,
    borderTopWidth,
    borderBottomWidth,
    minWidth
  } = _ref5;
  // Subtract border width from padding so overall button width/height doesn't
  // jump around if the border width changes (avoiding NaN and negative padding)
  const offsetBorder = _ref6 => {
    let {
      value,
      borderSize = borderWidth
    } = _ref6;
    return typeof value === 'number' && typeof borderSize === 'number' ? Math.max(0, value - borderSize) : value;
  };
  return {
    paddingLeft: offsetBorder({
      value: paddingLeft,
      borderSize: borderLeftWidth
    }),
    paddingRight: offsetBorder({
      value: paddingRight,
      borderSize: borderRightWidth
    }),
    paddingTop: offsetBorder({
      value: paddingTop,
      borderSize: borderTopWidth
    }),
    paddingBottom: offsetBorder({
      value: paddingBottom,
      borderSize: borderBottomWidth
    }),
    backgroundColor,
    minWidth,
    ...applyShadowToken(shadow)
  };
};
const selectBorderStyles = _ref7 => {
  let {
    borderColor,
    borderWidth,
    borderRadius,
    borderLeftWidth,
    borderRightWidth,
    borderTopWidth,
    borderBottomWidth
  } = _ref7;
  return {
    borderColor,
    borderWidth,
    borderRadius,
    borderLeftWidth,
    borderRightWidth,
    borderTopWidth,
    borderBottomWidth
  };
};
const selectTextStyles = (_ref8, themeOptions) => {
  let {
    fontSize,
    color,
    lineHeight,
    fontName,
    fontWeight,
    textAlign,
    textLine,
    textLineStyle
  } = _ref8;
  return applyTextStyles({
    fontSize,
    color,
    lineHeight,
    fontName,
    fontWeight,
    themeOptions,
    textAlign,
    textDecorationLine: textLine,
    textDecorationStyle: textLineStyle
  });
};
const selectWebOnlyStyles = (inactive, themeTokens, _ref9) => {
  let {
    accessibilityRole
  } = _ref9;
  return Platform.select({
    web: {
      // if it would overflow the container, wraps instead
      maxWidth: `calc(100% + ${getOuterBorderOffset(themeTokens) * 2}px)`,
      width: 'fit-content',
      outline: 'none',
      // removes the default browser :focus outline
      ...getCursorStyle(inactive, accessibilityRole)
    },
    default: {}
  });
};
const selectButtonStyles = _ref10 => {
  let {
    textAlign
  } = _ref10;
  return {
    flexDirection: 'row',
    justifyContent: textAlign
  };
};
const selectItemIconTokens = _ref11 => {
  let {
    color,
    iconColor,
    iconSize
  } = _ref11;
  return {
    size: iconSize,
    color: iconColor || color
  };
};
const ButtonBase = /*#__PURE__*/React.forwardRef((_ref12, ref) => {
  let {
    id,
    href,
    hrefAttrs,
    children,
    tokens = {},
    disabled = false,
    // alias for inactive
    inactive = disabled,
    selected = false,
    icon,
    iconPosition = icon ? 'left' : undefined,
    iconProps,
    ...rawRest
  } = _ref12;
  const {
    themeOptions
  } = useTheme();
  const {
    viewport
  } = rawRest;
  const enableMediaQueryStyleSheet = themeOptions.enableMediaQueryStyleSheet && viewport;
  const {
    onPress,
    ...rest
  } = clickProps.toPressProps(rawRest);
  const extraButtonState = {
    inactive,
    selected,
    iconPosition
  };
  const resolveButtonTokens = pressableState => resolvePressableTokens(tokens, pressableState, extraButtonState);
  const systemProps = selectProps(rest);
  let layoutMediaQueryStyles;
  let flexAndWidthStylesIds;
  if (enableMediaQueryStyleSheet) {
    const defaultPressableState = {
      pressed: false,
      hovered: false,
      focused: false
    };
    const defaultTokensByViewport = resolveButtonTokens(defaultPressableState);
    const layoutTokensByViewport = Object.entries(defaultTokensByViewport).reduce((acc, _ref13) => {
      let [vp, viewportTokens] = _ref13;
      const flexAndWidthStyles = viewportTokens.width === '100%' && viewportTokens.flex === 1 ? selectFlexAndWidthStyles(viewportTokens) : {};
      acc[vp] = {
        ...staticStyles.row,
        ...selectWebOnlyStyles(inactive, viewportTokens, systemProps),
        ...(Object.keys(flexAndWidthStyles).length > 0 ? flexAndWidthStyles : {}),
        ...selectOuterSizeStyles(viewportTokens)
      };
      return acc;
    }, {});
    const mediaQueryStyles = createMediaQueryStyles(layoutTokensByViewport);
    const {
      ids,
      styles
    } = StyleSheetUtils.create({
      layout: {
        ...mediaQueryStyles
      }
    });
    layoutMediaQueryStyles = styles.layout;
    flexAndWidthStylesIds = ids.layout;
  }
  const getPressableStyle = pressableState => {
    if (enableMediaQueryStyleSheet) {
      const themeTokens = resolveButtonTokens(pressableState)[viewport];
      return [layoutMediaQueryStyles, selectOuterContainerStyles(themeTokens)];
    }
    const themeTokens = resolveButtonTokens(pressableState);
    const flexAndWidthStyles = themeTokens.width === '100%' && themeTokens.flex === 1 ? selectFlexAndWidthStyles(themeTokens) : {};
    return [staticStyles.row, selectWebOnlyStyles(inactive, themeTokens, systemProps), selectOuterContainerStyles(themeTokens), ...(Object.keys(flexAndWidthStyles).length > 0 ? [flexAndWidthStyles] : []), selectOuterSizeStyles(themeTokens)];
  };
  const dataSetProp = flexAndWidthStylesIds || rawRest.dataSet ? {
    dataSet: {
      ...(flexAndWidthStylesIds ? {
        media: flexAndWidthStylesIds
      } : {}),
      ...rawRest.dataSet
    }
  } : {};
  return /*#__PURE__*/_jsx(Pressable, {
    ref: ref,
    href: href,
    onPress: linkProps.handleHref({
      href,
      onPress
    }),
    style: getPressableStyle,
    disabled: inactive,
    hrefAttrs: hrefAttrs,
    ...systemProps,
    ...dataSetProp,
    children: pressableState => {
      const themeTokens = enableMediaQueryStyleSheet ? resolveButtonTokens(pressableState)[viewport] : resolveButtonTokens(pressableState);
      const containerStyles = selectInnerContainerStyles(themeTokens);
      const borderStyles = selectBorderStyles(themeTokens);
      const textStyles = [selectTextStyles(themeTokens, themeOptions), staticStyles.text, Platform.select({
        web: {
          pointerEvents: 'none' // bubbles press event to parent `Pressable`
        }
      })];
      const iconTokens = selectItemIconTokens(themeTokens);
      const {
        iconSpace
      } = themeTokens;

      // If the container has a width set, fill it instead of sizing from content.
      // If in future we support text alignments other than center, add here.
      const stretchStyles = themeTokens.width ? staticStyles.stretch : staticStyles.align;
      const IconComponent = icon || themeTokens.icon;
      const rowStyles = selectButtonStyles(themeTokens);
      return /*#__PURE__*/_jsx(View, {
        id: id,
        style: [containerStyles, borderStyles, stretchStyles, staticStyles.row, Platform.select({
          web: {
            maxWidth: '100%',
            // ensure overflowing content wraps
            // TODO: https://github.com/telus/universal-design-system/issues/487
            transition: 'background-color 200ms, border-color 200ms',
            pointerEvents: 'none',
            // bubbles press event to parent `Pressable`
            ...rowStyles
          }
        })],
        children: /*#__PURE__*/_jsx(IconText, {
          icon: IconComponent,
          iconPosition: iconPosition,
          space: icon ? iconSpace : 0,
          iconProps: {
            ...iconProps,
            tokens: iconTokens
          },
          children: wrapStringsInText(typeof children === 'function' ? children({
            ...resolvePressableState(pressableState, extraButtonState),
            textStyles
          }) : children, {
            style: textStyles
          })
        })
      });
    }
  });
});
ButtonBase.displayName = 'ButtonBase';
ButtonBase.propTypes = {
  id: PropTypes.string,
  ...selectedSystemPropTypes,
  ...buttonPropTypes
};
const staticStyles = StyleSheet.create({
  row: {
    // Apply all button alignment horizontally; no vertical stacking within a button
    flexDirection: 'row',
    justifyContent: 'center'
  },
  text: {
    ...Platform.select({
      // TODO: https://github.com/telus/universal-design-system/issues/487
      web: {
        transition: 'color 200ms'
      }
    })
  },
  align: {
    alignItems: 'center'
  },
  stretch: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center'
  }
});
export default withLinkRouter(ButtonBase);